import { useCallback, useEffect, useRef, useState } from 'react';

import { reportImpression, useImpression } from '@v2/shared/lib/analytics';
import { PRODUCT_TYPE_POSTCARD } from '@v2/shared/lib/megaSale';

import ProductSkeleton from './ProductSkeleton';
import ProductCard, { ProductCardProps } from './ProductCard';

/**
 * The list the sale's tiles are reported under.
 *
 * The name is the legacy container's `data-list="Mega Sale"`, so both arms of the A/B test
 * report into one list and the comparison is a comparison.
 */
const SALE_LIST = 'Mega Sale';

/**
 * How far outside the viewport a tile is still drawn for real.
 *
 * Big enough that a normal scroll never reaches an undrawn tile — the swap happens most
 * of a screen ahead — and small enough that a sale of several hundred products keeps
 * only a couple of dozen mounted. The legacy component used 500px on a whole category at
 * a time; this is per tile, so the same distance buys a much smaller live set.
 */
const ROOT_MARGIN = '800px 0px';

type Props = ProductCardProps & {
  /**
   * Keeps this tile drawn whatever the scroll position — the product being scrolled to.
   *
   * It is the tile furthest from the viewport by definition, so without this there would
   * be nothing at the destination but a skeleton, and no destination to measure.
   */
  pinned?: boolean;
};

/**
 * One position in the grid: a real tile when it is near the viewport, a skeleton when it
 * is not.
 *
 * This is what makes a several-hundred-product sale usable. Every tile carries two
 * dropdowns, a dialog and its own pricing state, and mounting all of them costs seconds
 * of scripting and a great deal of memory on a phone. Only what is close to being looked
 * at is real.
 *
 * The skeleton is exactly the tile's size, so swapping between them moves nothing —
 * which is the property that makes it safe to unmount on the way out as well as mount on
 * the way in. Without it, scrolling back up would find the page had shifted underneath.
 */
export default function ProductSlot({ pinned = false, ...cardProps }: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [near, setNear] = useState(false);

  useEffect(() => {
    const element = ref.current;

    if (!element) {
      return;
    }

    // Guarded because jsdom and older Safari have no IntersectionObserver, and the
    // failure mode without it is a page of permanent skeletons — every product on the
    // sale invisible. Drawing everything is the correct fallback.
    if (typeof IntersectionObserver === 'undefined') {
      setNear(true);
      return;
    }

    const observer = new IntersectionObserver(([entry]) => setNear(entry.isIntersecting), {
      rootMargin: ROOT_MARGIN,
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  /*
   * The impression — reported when the tile has actually been on screen, and not when it
   * is merely drawn.
   *
   * A second watcher rather than the one above, because the two are asking different
   * questions: that one is 800px early on purpose, and an impression counted 800px before
   * a product is on screen is an impression for something nobody saw. `useImpression` has
   * no margin and waits out a dwell, so a tile the page scrolled past does not count as
   * one the reader looked at.
   *
   * The price is the sale document's rather than the term-adjusted one — which is what the
   * legacy tile put in `data-object-price`: at the moment a tile is revealed nothing has
   * been chosen on it. The category is the placement, so a title in two sections of the
   * sale counts twice, as it did on the legacy page, which tagged the tile and not the
   * product.
   */
  const impression = useImpression(() =>
    reportImpression({
      list: SALE_LIST,
      placement: String(cardProps.categoryId),
      productId: cardProps.product.id,
      baseSkuCode: cardProps.product.base_sku_code,
      name: cardProps.product.name,
      price: cardProps.product.price,
    })
  );

  // One element, two watchers. Composed through a callback of its own so the identity
  // stays put: an inline one would be a new function every render, which React answers by
  // detaching and reattaching both — restarting the dwell each time.
  const watch = useCallback(
    (node: HTMLDivElement | null) => {
      ref.current = node;
      impression(node);
    },
    [impression]
  );

  return (
    <div ref={watch} className="min-w-0">
      {near || pinned ? (
        <ProductCard {...cardProps} />
      ) : (
        <ProductSkeleton postcard={cardProps.product.product_type === PRODUCT_TYPE_POSTCARD} />
      )}
    </div>
  );
}
